/** Optional socials behind Add social. Website stays always visible. */

export const EXTRA_SOCIAL_NETWORKS = [
  'instagram',
  'facebook',
  'threads',
  'tiktok',
  'youtube',
  'substack',
  'github',
] as const

export const OPTIONAL_SOCIAL_NETWORKS = ['linkedin', 'x', ...EXTRA_SOCIAL_NETWORKS] as const

export type ExtraSocialNetwork = (typeof EXTRA_SOCIAL_NETWORKS)[number]
export type SocialContactNetwork = (typeof OPTIONAL_SOCIAL_NETWORKS)[number]

const OPTIONAL_SOCIAL_NETWORK_SET = new Set<string>(OPTIONAL_SOCIAL_NETWORKS)
const EXTRA_SOCIAL_NETWORK_SET = new Set<string>(EXTRA_SOCIAL_NETWORKS)
const CORE_SOCIAL_NETWORKS: SocialContactNetwork[] = ['linkedin', 'x']

export type ExtraSocialFields = {
  instagram_url: string
  facebook_url: string
  threads_url: string
  tiktok_url: string
  youtube_url: string
  substack_url: string
  github_url: string
}

export const EMPTY_EXTRA_SOCIAL_FIELDS: ExtraSocialFields = {
  instagram_url: '',
  facebook_url: '',
  threads_url: '',
  tiktok_url: '',
  youtube_url: '',
  substack_url: '',
  github_url: '',
}

export type ExtraSocialContact = ExtraSocialFields & {
  extra_social_order: SocialContactNetwork[]
}

export const SOCIAL_FIELD_BY_NETWORK: Record<SocialContactNetwork, string> = {
  linkedin: 'linkedin_url',
  x: 'x_url',
  instagram: 'instagram_url',
  facebook: 'facebook_url',
  threads: 'threads_url',
  tiktok: 'tiktok_url',
  youtube: 'youtube_url',
  substack: 'substack_url',
  github: 'github_url',
}

export const SOCIAL_NETWORK_LABEL: Record<SocialContactNetwork, string> = {
  linkedin: 'LinkedIn',
  x: 'X (Twitter)',
  instagram: 'Instagram',
  facebook: 'Facebook',
  threads: 'Threads',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  substack: 'Substack',
  github: 'GitHub',
}

export type ExtraSocialSource = {
  linkedin_url?: string | null
  x_url?: string | null
} & {
  [K in keyof ExtraSocialFields]?: string | null
} & {
  extra_social_order?: string[] | null
}

export function extraSocialFieldKey(network: ExtraSocialNetwork): keyof ExtraSocialFields {
  return `${network}_url`
}

export function socialFieldKey(network: SocialContactNetwork): string {
  return SOCIAL_FIELD_BY_NETWORK[network]
}

export function extraSocialFieldsFrom(source: ExtraSocialSource | null | undefined): ExtraSocialFields {
  return {
    instagram_url: source?.instagram_url ?? '',
    facebook_url: source?.facebook_url ?? '',
    threads_url: source?.threads_url ?? '',
    tiktok_url: source?.tiktok_url ?? '',
    youtube_url: source?.youtube_url ?? '',
    substack_url: source?.substack_url ?? '',
    github_url: source?.github_url ?? '',
  }
}

export function extraSocialFieldKeys(): (keyof ExtraSocialFields)[] {
  return EXTRA_SOCIAL_NETWORKS.map(extraSocialFieldKey)
}

export function isExtraSocialNetwork(value: string): value is ExtraSocialNetwork {
  return EXTRA_SOCIAL_NETWORK_SET.has(value)
}

export function isOptionalSocialNetwork(value: string): value is SocialContactNetwork {
  return OPTIONAL_SOCIAL_NETWORK_SET.has(value)
}

/** Saved add-order, ignoring unknown names and duplicates. */
export function normalizeExtraSocialOrder(raw: string[] | null | undefined): SocialContactNetwork[] {
  if (!raw?.length) return []
  const seen = new Set<SocialContactNetwork>()
  const ordered: SocialContactNetwork[] = []
  for (const item of raw) {
    const network = item.trim().toLowerCase().replace(/_url$/, '')
    if (!isOptionalSocialNetwork(network) || seen.has(network)) continue
    seen.add(network)
    ordered.push(network)
  }
  return ordered
}

function filledExtraNetworks(fields: ExtraSocialFields): ExtraSocialNetwork[] {
  return EXTRA_SOCIAL_NETWORKS.filter((network) => fields[extraSocialFieldKey(network)].trim())
}

function filledOptionalNetworks(
  source: ExtraSocialSource | null | undefined,
  fields: ExtraSocialFields,
): SocialContactNetwork[] {
  const filled: SocialContactNetwork[] = []
  if (source?.linkedin_url?.trim()) filled.push('linkedin')
  if (source?.x_url?.trim()) filled.push('x')
  filled.push(...filledExtraNetworks(fields))
  return filled
}

function withLegacyCorePrefix(
  ordered: SocialContactNetwork[],
  filled: SocialContactNetwork[],
): SocialContactNetwork[] {
  const hasCore = ordered.some((network) => network === 'linkedin' || network === 'x')
  const next = hasCore
    ? [...ordered]
    : [...CORE_SOCIAL_NETWORKS.filter((network) => filled.includes(network)), ...ordered]
  for (const network of filled) {
    if (!next.includes(network)) next.push(network)
  }
  return next
}

/**
 * Filled socials in the order they were added. Empty networks are omitted.
 * LinkedIn/X missing from older saved lists stay first. Filled extras missing
 * from the saved list are appended.
 */
export function resolveExtraSocialOrder(
  source: ExtraSocialSource | null | undefined,
  fields: ExtraSocialFields = extraSocialFieldsFrom(source),
): SocialContactNetwork[] {
  const filled = filledOptionalNetworks(source, fields)
  const filledSet = new Set(filled)
  const ordered = normalizeExtraSocialOrder(source?.extra_social_order).filter((network) =>
    filledSet.has(network),
  )
  return withLegacyCorePrefix(ordered, filled)
}

/**
 * Editor order: keep socials in add-order, including empty ones just added.
 * Filled LinkedIn/X missing from older lists stay first.
 */
export function extraSocialOrderForEdit(
  source: ExtraSocialSource | null | undefined,
  fields: ExtraSocialFields = extraSocialFieldsFrom(source),
): SocialContactNetwork[] {
  const ordered = [...normalizeExtraSocialOrder(source?.extra_social_order)]
  return withLegacyCorePrefix(ordered, filledOptionalNetworks(source, fields))
}

export function extraSocialContactFrom(
  source: ExtraSocialSource | null | undefined,
): ExtraSocialContact {
  const fields = extraSocialFieldsFrom(source)
  return {
    ...fields,
    extra_social_order: resolveExtraSocialOrder(source, fields),
  }
}

export function extraSocialContactForEdit(
  source: ExtraSocialSource | null | undefined,
): ExtraSocialContact {
  const fields = extraSocialFieldsFrom(source)
  return {
    ...fields,
    extra_social_order: extraSocialOrderForEdit(source, fields),
  }
}

export function socialUrlFrom(
  source: ExtraSocialSource | null | undefined,
  network: SocialContactNetwork,
): string {
  if (network === 'linkedin') return source?.linkedin_url ?? ''
  if (network === 'x') return source?.x_url ?? ''
  return extraSocialFieldsFrom(source)[extraSocialFieldKey(network)]
}
